import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { backofficeClient } from "@/lib/api/backoffice-client";
import type { CredentialSummary } from "@/hooks/use-itsm-import";
import type {
  IntegrationConfigBody,
  IntegrationConfigRead,
  ProviderConfig,
} from "@/lib/types/itsm-config";

const CREDENTIALS_PATH = "/proxy/integrations/api/v1/credentials";
const SYNC_STATE_PATH = "/proxy/itsm-integration-collector/api/v1/sync-state";

/** Ongoing (scheduled) sync state for an integration — the "cursor". */
export interface SyncState {
  integration_id: string;
  provider: string;
  status: "active" | "failed" | "paused" | "inactive";
  last_sync_at: string | null;
  sync_cursor: string | null;
  consecutive_failures: number;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface CredentialsListResponse {
  credentials: CredentialSummary[];
  total: number;
}

// ITSM / project-management providers whose config we edit here. We filter
// by provider (NOT category) because ITSM config can live on a credential
// categorized as "saas-apps" — e.g. the "jira" (Jira Software) provider,
// which is where outbound export config is stored.
const ITSM_PROVIDERS = new Set([
  "jira",
  "jsm",
  "servicenow",
  "servicenow-serviceaccount",
  "freshservice",
  "asana",
  "monday",
  "linear",
]);

/** List a tenant's ITSM/PM integration credentials (the "integrations" picker). */
export function useItsmCredentials(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["itsm-config", "credentials", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<CredentialsListResponse>(
        CREDENTIALS_PATH,
        { params: { tenant_id: tenantId } },
      );
      return data.credentials.filter((c) => ITSM_PROVIDERS.has(c.provider));
    },
    enabled: Boolean(tenantId),
  });
}

/** Read the full integration config for a credential (empty if unset). */
export function useIntegrationConfig(
  tenantId: string | undefined,
  credentialId: string | undefined,
) {
  return useQuery({
    queryKey: ["itsm-config", "config", tenantId, credentialId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<IntegrationConfigRead>(
        `${CREDENTIALS_PATH}/${credentialId}/config`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(credentialId),
  });
}

/**
 * The integration's tenant-wide Jira `sync_linked_tickets` setting — what an
 * import job set to "inherit" resolves to. Undefined while loading or when no
 * credential is given.
 */
export function useIntegrationSyncLinkedTickets(
  tenantId: string | undefined,
  credentialId: string | undefined,
): boolean | undefined {
  const { data } = useIntegrationConfig(tenantId, credentialId);
  if (!data) return undefined;
  return data.config.provider_config?.sync_linked_tickets ?? false;
}

/**
 * Upsert the whole config (wholesale replace). The API has no per-rule
 * endpoint, so callers read the current config, mutate the relevant rule
 * list, and pass the full body here — preserving tags / external_categories
 * / other provider fields.
 */
export function useUpsertIntegrationConfig(
  tenantId: string,
  credentialId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: IntegrationConfigBody) => {
      const { data } = await backofficeClient.put<IntegrationConfigRead>(
        `${CREDENTIALS_PATH}/${credentialId}/config?tenant_id=${tenantId}`,
        { config: body },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["itsm-config", "config", tenantId, credentialId],
      });
    },
  });
}

/**
 * Build the next full config body from the current one, replacing just the
 * provider_config while preserving tags / external_categories. Ensures the
 * provider discriminator is always present.
 */
export function withProviderConfig(
  current: IntegrationConfigBody | undefined,
  provider: string,
  nextProviderConfig: ProviderConfig,
): IntegrationConfigBody {
  return {
    ...(current ?? {}),
    provider_config: {
      ...nextProviderConfig,
      provider: provider as ProviderConfig["provider"],
    },
  };
}

/**
 * Read the ongoing sync state (the "cursor") for an integration. Returns
 * null when the collector has never synced this integration (404) — that's
 * a normal "not synced yet" state, not an error.
 */
export function useSyncState(
  tenantId: string | undefined,
  integrationId: string | undefined,
) {
  return useQuery({
    queryKey: ["itsm-config", "sync-state", tenantId, integrationId],
    queryFn: async () => {
      try {
        const { data } = await backofficeClient.get<SyncState>(
          `${SYNC_STATE_PATH}/${integrationId}`,
          { params: { tenant_id: tenantId } },
        );
        return data;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: Boolean(tenantId) && Boolean(integrationId),
  });
}

/** Move the sync cursor (last_sync_at) for an integration. */
export function useUpdateSyncState(tenantId: string, integrationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (lastSyncAt: string) => {
      const { data } = await backofficeClient.patch<SyncState>(
        `${SYNC_STATE_PATH}/${integrationId}?tenant_id=${tenantId}`,
        { last_sync_at: lastSyncAt },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["itsm-config", "sync-state", tenantId, integrationId],
      });
    },
  });
}
